"use client";

import type { KioskDevToolProps } from "@/app/directories/kiosk/devtool/types";
import { getKioskIdentity } from "@/app/directories/kiosk/lib/kiosk-identity";
import { getBuildVersion } from "@/lib/app-version";
import { useState } from "react";

type ConfigHealthCheck = NonNullable<KioskDevToolProps["configHealthCheck"]>;

interface HealthCheckTabProps {
  configHealthCheck?: ConfigHealthCheck;
  onRerunHealthCheck?: () => void;
}

const OUTCOME_STYLES: Record<
  ConfigHealthCheck["configOutcome"],
  { card: string; label: string }
> = {
  healthy: {
    card: "bg-green-50 border-green-200",
    label: "text-green-900",
  },
  degraded: {
    card: "bg-yellow-50 border-yellow-200",
    label: "text-yellow-900",
  },
  blocked: {
    card: "bg-red-50 border-red-200",
    label: "text-red-900",
  },
};

function DiagnosticRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 border-b border-gray-100 last:border-b-0">
      <span className="text-xs font-medium text-gray-600 shrink-0">
        {label}
      </span>
      <span className="text-xs font-mono text-gray-900 break-all text-right">
        {value}
      </span>
    </div>
  );
}

function formatBoolean(value: boolean | undefined): string {
  if (value === undefined) return "unknown";
  return value ? "true" : "false";
}

export function HealthCheckTab({
  configHealthCheck,
  onRerunHealthCheck,
}: HealthCheckTabProps) {
  const [sentAt, setSentAt] = useState<string | null>(null);

  if (!configHealthCheck) {
    return (
      <div className="space-y-4 p-4">
        <p className="text-sm text-gray-600">Health check data unavailable</p>
      </div>
    );
  }

  const { kioskId, kioskName, kioskLocation } = getKioskIdentity();
  const shortKioskId = kioskId.slice(0, 8);
  const buildVersion = getBuildVersion();
  const outcomeStyles = OUTCOME_STYLES[configHealthCheck.configOutcome];

  const handleSend = () => {
    onRerunHealthCheck?.();
    setSentAt(new Date().toLocaleTimeString());
  };

  return (
    <div className="space-y-4 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Config Health Check
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          The exact practice/location resolution this kiosk is running with
          right now — the same data behind the `kiosk_config_resolved` Datadog
          event.
        </p>
      </div>

      <div className={`rounded-lg border p-4 ${outcomeStyles.card}`}>
        <div className={`text-sm font-semibold ${outcomeStyles.label}`}>
          {configHealthCheck.configOutcome.toUpperCase()}
        </div>
        <div className="text-xs text-gray-700 mt-1 font-mono">
          {configHealthCheck.configState}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3">
        <DiagnosticRow
          label="Practice ID"
          value={configHealthCheck.practiceId ?? "none"}
        />
        <DiagnosticRow
          label="Practice ID source"
          value={configHealthCheck.practiceIdSource}
        />
        <DiagnosticRow
          label="EHR location ID"
          value={configHealthCheck.ehrLocationId ?? "none"}
        />
        <DiagnosticRow
          label="EHR location ID source"
          value={configHealthCheck.ehrLocationIdSource}
        />
        <DiagnosticRow
          label="Practice found in org"
          value={formatBoolean(configHealthCheck.practiceFoundInOrg)}
        />
        <DiagnosticRow
          label="Practice fetch failed"
          value={formatBoolean(configHealthCheck.practiceFetchFailed)}
        />
        <DiagnosticRow
          label="Restored from stateId"
          value={formatBoolean(configHealthCheck.restoredFromStateId)}
        />
        <DiagnosticRow label="Kiosk ID" value={shortKioskId} />
        <DiagnosticRow label="Kiosk name" value={kioskName ?? "none"} />
        <DiagnosticRow label="Kiosk location" value={kioskLocation ?? "none"} />
        <DiagnosticRow label="Build version" value={buildVersion} />
      </div>

      <button
        type="button"
        onClick={handleSend}
        disabled={!onRerunHealthCheck}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
      >
        Send health check to Datadog
      </button>

      {sentAt && (
        <p className="text-xs text-gray-600 text-center">
          Sent at {sentAt} (tagged manual)
        </p>
      )}
    </div>
  );
}
